import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import { createProductSolutionCategory, getAdminProductSolutionCategoryById, updateProductSolutionCategory } from 'apis/adminProductSolutionCategories';
import { getAdminProductSolutionGroups } from 'apis/adminProductSolutionGroups';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';


const ProductSolutionCategoriesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        productSolutionGroupId: '',
        image: '',
        productUrl: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery('product-solution-groups-options', { pageSize: 1000, languageCode: 'vi' }, getAdminProductSolutionGroups);
    const groups = groupsData.items;
    const { data: detail, error: detailError } = useAdminDetailQuery('product-solution-categories', id, getAdminProductSolutionCategoryById);
    const createMutation = useAdminCreateMutation('product-solution-categories', createProductSolutionCategory);
    const updateMutation = useAdminUpdateMutation('product-solution-categories', updateProductSolutionCategory);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, name: '', slug: '', description: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setFormData({ ...detail, productSolutionGroupId: detail.productSolutionGroupId || '', productUrl: detail.productUrl || '', translation: detail.translation || [] });
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                productSolutionGroupId: formData.productSolutionGroupId ? Number(formData.productSolutionGroupId) : null,
                image: formData.image,
                productUrl: formData.productUrl || null,
                sortOrder: Number(formData.sortOrder),
                isActive: formData.isActive,
                translation: languages.map(lang => {
                    const t = formData.translation.find(x => x.languageId === lang.id) || {};
                    return {
                        languageId: lang.id,
                        name: t.name || '',
                        slug: t.slug || '',
                        description: t.description || ''
                    };
                }),
            };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/product-solution-categories');
            } else {
                toast.error(res.message || 'Lưu thất bại');
            }
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Danh mục Sản phẩm / Giải pháp' : 'Thêm Danh mục Sản phẩm / Giải pháp'} />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt chung</p>
                <div className="form-row">
                    <Select
                        label="Nhóm Sản phẩm / Giải pháp *"
                        name="productSolutionGroupId"
                        value={formData.productSolutionGroupId}
                        onChange={handleChange}
                        required
                        options={[
                            { value: '', label: '— Chọn nhóm —' },
                            ...groups.map(g => ({ value: g.id, label: g.name || `Nhóm #${g.id}` })),
                        ]}
                    />
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                </div>
                <Input
                    label="Link sản phẩm thực tế (nếu có)"
                    name="productUrl"
                    value={formData.productUrl || ''}
                    onChange={handleChange}
                    placeholder="Ví dụ: https://i-office.hsv.com.vn hoặc /san-pham-giai-phap/..."
                />
                <ImageUploadField
                    label="Hình ảnh danh mục"
                    value={formData.image}
                    folder="product-solutions/categories"
                    onChange={(image) => setFormData(prev => ({ ...prev, image }))}
                />
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tên *" value={t.name || ''} onChange={e => handleTranslationChange(lang.id, 'name', e.target.value)} required />
                                        <Input label="Slug" value={t.slug || ''} onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)} />
                                        <Input label="Mô tả" value={t.description || ''} onChange={e => handleTranslationChange(lang.id, 'description', e.target.value)} />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}
                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/product-solution-categories')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default ProductSolutionCategoriesForm;
